export function ltlFieldInputId(fieldKey: string): string {
  if (fieldKey === "pickupOpenTime" || fieldKey === "pickupCloseTime") {
    return bolTimeInputId(fieldKey as "pickupOpenTime" | "pickupCloseTime");
  }
  return `ltl-field-${fieldKey}`;
}
export function bolTimeInputId(name: "pickupOpenTime" | "pickupCloseTime"): string {
  return `ltl-bol-time-${name}`;
}

const BOL_DETAIL_FIELD_KEYS = new Set<string>([
  "originAddress",
  "originAddress2",
  "destinationAddress",
  "destinationAddress2",
  "shipperName",
  "consigneeName",
  "companyName",
  "pickupOpenTime",
  "pickupCloseTime",
  "poNumber",
  "billOfLadingNote",
  "shipperNumber",
  "shipperContact",
  "shipperPhone",
  "consigneeContact",
  "consigneePhone",
]);
const STATIC_FIELD_PRIORITY: string[] = [
  "originZip",
  "originState",
  "destinationZip",
  "destinationState",
  "pickupDate",
  "originAddress",
  "originAddress2",
  "destinationAddress",
  "destinationAddress2",
  "shipperName",
  "shipperNumber",
  "pickupOpenTime",
  "pickupCloseTime",
];
const ITEM_SUBKEY_ORDER = ["class", "weight", "nmfc", "length", "width", "height", "pieces"];

function itemErrorKeys(errors: Record<string, string>): string[] {
  return Object.keys(errors)
    .filter((k) => /^item\d+_/.test(k))
    .sort((a, b) => {
      const ma = a.match(/^item(\d+)_(.+)$/);
      const mb = b.match(/^item(\d+)_(.+)$/);
      const ia = ma ? parseInt(ma[1], 10) : 0;
      const ib = mb ? parseInt(mb[1], 10) : 0;

      if (ia !== ib) return ia - ib;
      const sa = ma?.[2] ?? "";
      const sb = mb?.[2] ?? "";
      const ra = ITEM_SUBKEY_ORDER.indexOf(sa);
      const rb = ITEM_SUBKEY_ORDER.indexOf(sb);

      return (ra === -1 ? 99 : ra) - (rb === -1 ? 99 : rb);
    });
}

export function getFirstLtlValidationErrorKey(errors: Record<string, string>): string | undefined {
  for (const k of STATIC_FIELD_PRIORITY) {
    if (errors[k]) return k;
  }
  for (const k of itemErrorKeys(errors)) {
    if (errors[k]) return k;
  }
  if (errors.equipment) return "equipment";
  const rest = Object.keys(errors).filter(Boolean);

  return rest[0];
}
export type LtlDetailsTab = "shipment" | "bol";
export function getLtlDetailsTabForError(
  fieldKey: string,
  opts: {
    requiresNmfcPerItem?: boolean;
  }
): LtlDetailsTab {
  if (opts.requiresNmfcPerItem && /^item\d+_nmfc$/.test(fieldKey)) {
    return "bol";
  }
  if (BOL_DETAIL_FIELD_KEYS.has(fieldKey)) {
    return "bol";
  }
  return "shipment";
}

const FIELD_LABELS: Record<string, string> = {
  originZip: "Origin ZIP",
  originState: "Origin state",
  destinationZip: "Destination ZIP",
  destinationState: "Destination state",
  pickupDate: "Pickup date",
  originAddress: "Origin address",
  originAddress2: "Origin address 2",
  destinationAddress: "Destination address",
  destinationAddress2: "Destination address 2",
  shipperName: "Shipper name",
  consigneeName: "Consignee name",
  companyName: "Company name",
  pickupOpenTime: "Pickup open time",
  pickupCloseTime: "Pickup close time",
  poNumber: "PO #",
  billOfLadingNote: "BOL notes",
  shipperNumber: "Shipper number",
  shipperContact: "Shipper contact",
  shipperPhone: "Shipper phone",
  consigneeContact: "Consignee contact",
  consigneePhone: "Consignee phone",
  equipment: "Equipment",
};

export function humanReadableLtlFieldLabel(fieldKey: string): string {
  if (fieldKey === "equipment") return FIELD_LABELS.equipment;
  const m = fieldKey.match(/^item(\d+)_(.+)$/);

  if (m) {
    const idx = Number(m[1]) + 1;
    const sub = m[2];

    if (sub === "class") return `Item ${idx}: freight class`;
    if (sub === "weight") return `Item ${idx}: weight`;
    if (sub === "nmfc") return `Item ${idx}: NMFC`;
    if (sub === "length" || sub === "width" || sub === "height") return `Item ${idx}: dimensions`;
    if (sub === "pieces") return `Item ${idx}: pieces`;
    return `Item ${idx}: ${sub}`;
  }
  return (
    FIELD_LABELS[fieldKey] ??
    fieldKey.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())
  );
}
export function summarizeLtlValidationErrors(
  errors: Record<string, string>,
  maxLabels = 6
): string {
  const keys = Object.keys(errors).filter((k) => errors[k]);

  if (keys.length === 0) return "";
  const labels = keys.slice(0, maxLabels).map((k) => humanReadableLtlFieldLabel(k));
  const extra = keys.length > maxLabels ? ` (+${keys.length - maxLabels} more)` : "";

  return `Complete or fix: ${labels.join(", ")}${extra}.`;
}
export function focusLtlValidationError(fieldKey: string): void {
  let el: HTMLElement | null = null;

  if (fieldKey === "equipment") {
    const wrap = document.getElementById(ltlFieldInputId("equipment"));

    el = (wrap?.querySelector(".custom-dropdown-selected") as HTMLElement | null) ?? wrap;
  } else {
    el = document.getElementById(ltlFieldInputId(fieldKey));
  }
  if (!el) {
    return;
  }
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  try {
    el.focus({ preventScroll: true });
  } catch {
    el.focus();
  }
  if (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA" && el.tagName !== "SELECT") {
    const inner = el.querySelector?.("input, textarea, select") as HTMLElement | null;

    try {
      inner?.focus({ preventScroll: true });
    } catch {
      inner?.focus();
    }
  }
}
